/**
 * Typed Message - Discriminated Union for Message Content
 *
 * THE WHITE RABBIT SOLUTION:
 * Use TypeScript's discriminated unions to automatically narrow message content types
 * based on the `contentType` field. Zero runtime cost, 100% type safety!
 *
 * NO MORE CASTING NEEDED - TypeScript is smart enough to figure it out!
 */

import type { DbEntity } from "../../../generated/prisma-models";
import type { JsonValue } from "../../core";
import type { CodeInterpreterBlock, CodeInterpreterFile, MessageFile } from "./chat.types";
import type { MessageContent } from "./message-content.types";

// ============================================================================
// CONTENT TYPE DEFINITIONS
// ============================================================================

/**
 * Standard text/image message content
 */
export interface TextMessageContent {
  text: string;
  images?: string[];
}

/**
 * AI message content with code interpreter data
 */
export interface CodeInterpreterMessageContent extends TextMessageContent {
  codeInterpreterBlocks?: CodeInterpreterBlock[];
  codeInterpreterFiles?: CodeInterpreterFile[];
}

/**
 * File message content (single file)
 */
export interface FileMessageContent {
  file: {
    name: string;
    url: string;
    type?: string;
    size?: number;
  };
}

/**
 * Files message content (multiple files)
 */
export interface FilesMessageContent {
  files: Array<{
    name: string;
    url: string;
    type?: string;
    size?: number;
  }>;
}

/**
 * Tool output message content
 */
export interface ToolOutputMessageContent {
  output?: unknown;
  result?: unknown;
  error?: string;
  [key: string]: unknown;
}

// ============================================================================
// TYPED MESSAGE CONTENT
// ============================================================================

/**
 * Message content properly typed based on contentType
 *
 * TypeScript will automatically narrow based on contentType:
 * if (message.contentType === 'HUMAN' && typeof message.content === 'object') {
 *   message.content.text // ✅ TypeScript knows it's TextMessageContent!
 * }
 */
export type TypedMessageContent =
  | string // Simple string content
  | TextMessageContent // Standard structured content
  | CodeInterpreterMessageContent // AI message with code interpreter blocks/files
  | FileMessageContent // Single file
  | FilesMessageContent // Multiple files
  | ToolOutputMessageContent; // Tool output

// ============================================================================
// TYPED CHAT MESSAGE
// ============================================================================

/**
 * Message with properly typed content
 *
 * Content type is inferred from contentType field:
 * - HUMAN/AI: Usually TextMessageContent or string
 * - TOOL/FUNCTION: Usually ToolOutputMessageContent
 *
 * `content` is nullable because its column is `Message.content Json?`: the
 * database admits a row with no content, and the message APIs pass it through
 * as `null` (`MessageSchema.content` is `.nullable()`). This type narrows WHAT
 * a present content may be; it does not get to decide that content is present.
 * Every reader says what it does with the absence.
 */
export type TypedChatMessage = Omit<DbEntity.Message, "content"> & {
  content: TypedMessageContent | null;
  /**
   * Optional files attached to the message (Knowledge records).
   * Present when backend includes files relation in query.
   */
  files?: MessageFile[];
};

/**
 * The agent fields a chat transcript renders for a message's author.
 *
 * Declared as the two fields the renderer actually reads — `id` for the link to
 * the agent's prompt tab, `profile_picture` for the avatar — rather than as a
 * whole agent. Two producers legitimately hold DIFFERENT agent types and only
 * this shape is common to both:
 *
 *   - `DbEntity.Agent` (Prisma), where `profile_picture` is `string`
 *   - the API `AgentSchema`, where it is `string | null`
 *
 * `string | null` is therefore the honest spelling: it is what the Playground
 * store holds, and widening it to `string` is what two `as unknown as` casts at
 * `MetaConversationGroup`'s call sites used to launder. A consumer handing the
 * value to something requiring `string` must say what it does with the absence.
 *
 * Sibling of `MessageFileRelation` below, and of `FileAttachment` in
 * `apps/frontend/src/common/components/Chat/EnhancedMessage/FileList.tsx`, which solve the
 * same problem for the `files` relation.
 */
export interface MessageAuthorAgent {
  id: string;
  profile_picture: string | null;
}

/**
 * Message file from Prisma relation (Knowledge model subset)
 * Matches the MessageFile interface for file display
 */
export interface MessageFileRelation {
  id: string;
  name: string;
  textContent?: string | null;
}

/**
 * Message with optional Prisma relations
 *
 * THE WHITE RABBIT: When backend includes relations in query,
 * use this type to safely access them without `as any`
 *
 * Relations are optional - only present when explicitly included in Prisma query
 */
export type MessageWithRelations = TypedChatMessage & {
  // Prisma relation fields (only present when included)
  files?: MessageFileRelation[];
  satisfactionScore?: {
    score: number;
  } | null;
  // Add other relations as needed
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Safely extract text from message content
 *
 * Handles all content types and returns the text or undefined
 * Accepts JsonValue from Prisma to work with database types
 */
export function getMessageText(
  content: TypedMessageContent | string | MessageContent | JsonValue | null | undefined
): string | undefined {
  if (!content) return undefined;

  if (typeof content === "string") {
    return content;
  }

  if (typeof content === "object" && "text" in content) {
    const text = content.text;
    // Handle case where text is a string (normal case)
    if (typeof text === "string") {
      return text;
    }

    // Handle case where text is an array (e.g., from streaming or malformed data)
    if (Array.isArray(text)) {
      // Extract text from array of objects or strings
      const extractedTexts = text
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }
          if (typeof item === "object" && item !== null) {
            // Try to extract values from object (e.g., {"hey":""} -> "hey")
            const values = Object.values(item).filter((v) => v && typeof v === "string");
            if (values.length > 0) {
              return values.join(" ");
            }
            // If no string values, try to get keys
            const keys = Object.keys(item).filter((k) => k);
            if (keys.length > 0) {
              return keys.join(" ");
            }
          }
          return null;
        })
        .filter((t): t is string => t !== null && t !== "");

      return extractedTexts.length > 0 ? extractedTexts.join("\n") : undefined;
    }

    // Handle case where text is an object (malformed data)
    if (typeof text === "object" && text !== null) {
      // Try to extract string values from the object
      const values = Object.values(text).filter((v) => v && typeof v === "string");
      if (values.length > 0) {
        return values.join(" ");
      }
      // If no string values, try to get keys
      const keys = Object.keys(text).filter((k) => k);
      if (keys.length > 0) {
        return keys.join(" ");
      }
      // Last resort: stringify
      try {
        return JSON.stringify(text);
      } catch {
        return String(text);
      }
    }
    if (text === null) {
      return undefined;
    }
    return String(text);
  }

  return undefined;
}

/**
 * Extract audioUrl from message content (voice call per-turn audio).
 * Returns the S3 URL if present, undefined otherwise.
 */
export function getMessageAudioUrl(
  content: TypedMessageContent | string | MessageContent | JsonValue | null | undefined
): string | undefined {
  if (!content || typeof content !== "object" || Array.isArray(content)) return undefined;
  if ("audioUrl" in content && typeof content.audioUrl === "string") {
    return content.audioUrl;
  }
  return undefined;
}

/**
 * Check if content has text field
 * Accepts JsonValue from Prisma to work with database types
 */
export function hasTextContent(
  content: TypedMessageContent | string | MessageContent | JsonValue | null | undefined
): content is TextMessageContent {
  return (
    content !== null &&
    content !== undefined &&
    typeof content === "object" &&
    "text" in content &&
    typeof content.text === "string"
  );
}

/**
 * Check if content is a file
 */
export function hasFileContent(
  content: TypedMessageContent | null | undefined
): content is FileMessageContent {
  return (
    content !== null &&
    content !== undefined &&
    typeof content === "object" &&
    "file" in content &&
    content.file !== undefined
  );
}

/**
 * Check if content has multiple files
 */
export function hasFilesContent(
  content: TypedMessageContent | null | undefined
): content is FilesMessageContent {
  return (
    content !== null &&
    content !== undefined &&
    typeof content === "object" &&
    "files" in content &&
    Array.isArray(content.files)
  );
}

/**
 * Check if content is tool output
 */
export function isToolOutputContent(
  content: TypedMessageContent | null | undefined
): content is ToolOutputMessageContent {
  return (
    content !== null &&
    content !== undefined &&
    typeof content === "object" &&
    !("text" in content) &&
    !("file" in content) &&
    !("files" in content)
  );
}

/**
 * Check if content has code interpreter data (blocks or files)
 */
export function hasCodeInterpreterContent(
  content: TypedMessageContent | JsonValue | null | undefined
): content is CodeInterpreterMessageContent {
  return (
    content !== null &&
    content !== undefined &&
    typeof content === "object" &&
    ("codeInterpreterBlocks" in content || "codeInterpreterFiles" in content)
  );
}

/**
 * Extract code interpreter blocks from message content (type-safe)
 */
export function getCodeInterpreterBlocks(
  content: TypedMessageContent | JsonValue | null | undefined
): CodeInterpreterBlock[] | undefined {
  if (hasCodeInterpreterContent(content)) {
    return content.codeInterpreterBlocks as CodeInterpreterBlock[] | undefined;
  }
  return undefined;
}

/**
 * Extract code interpreter files from message content (type-safe)
 */
export function getCodeInterpreterFiles(
  content: TypedMessageContent | JsonValue | null | undefined
): CodeInterpreterFile[] | undefined {
  if (hasCodeInterpreterContent(content)) {
    return content.codeInterpreterFiles as CodeInterpreterFile[] | undefined;
  }
  return undefined;
}

/**
 * Check if message content has renderable/displayable content.
 *
 * Used to filter out empty messages (e.g., AI messages with only tool_calls and no text).
 * Returns true if content has:
 * - Non-empty text string
 * - A `type` field (tool results like workflow_executed)
 * - Images array with content
 * - A rendered HTML template card
 * - Code-interpreter blocks or generated files
 *
 * 🔴 THE CODE-INTERPRETER TERMS ARE NOT DECORATION: a turn whose only payload is a
 * file the interpreter generated FOR the customer carries no text, no `type` and no
 * images, so without them this returned false and both callers DROPPED the row —
 * `embedReducers/streaming.ts` and the playground's `streaming.ts` each treat a
 * false here as "empty, discard". The file was the answer the customer asked for.
 *
 * Both halves are counted on `.length > 0` rather than presence, so an empty array
 * does not keep an otherwise-empty row alive. That is deliberately STRICTER than
 * `EnhancedMessage`'s `hasPersistedCodeInterpreterBlocks`, which tests
 * `Array.isArray` and therefore admits `[]`; this one gates ADMISSION to the
 * transcript, where a row with nothing to draw must stay out.
 */
export function hasRenderableContent(
  content: TypedMessageContent | string | MessageContent | JsonValue | null | undefined
): boolean {
  if (!content) return false;

  // String content
  if (typeof content === "string") {
    return content.trim().length > 0;
  }

  // Object content
  if (typeof content === "object" && content !== null) {
    const c = content as Record<string, unknown>;

    // Has non-empty text content
    if (typeof c.text === "string" && c.text.trim().length > 0) return true;

    // Has a type field (tool results like workflow_executed, workflow_execution_started)
    if (c.type) return true;

    // Has images
    if (Array.isArray(c.images) && c.images.length > 0) return true;

    // Has a rendered HTML template card (may arrive with an empty text fallback)
    const htmlTemplate = c.htmlTemplate as { html?: unknown } | undefined;
    if (htmlTemplate && typeof htmlTemplate.html === "string" && htmlTemplate.html.length > 0) {
      return true;
    }

    // Has code-interpreter blocks the surface draws (reasoning, prose, tool calls)
    if (Array.isArray(c.codeInterpreterBlocks) && c.codeInterpreterBlocks.length > 0) return true;

    // Has files the interpreter generated for the customer to download
    if (Array.isArray(c.codeInterpreterFiles) && c.codeInterpreterFiles.length > 0) return true;
  }

  return false;
}
